import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { modulesAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UIKit/Button';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import SearchInput from '../components/UIKit/SearchInput';
import ModuleDisplayCard from '../components/ModuleDisplayCard';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  FilterListOff as FilterListOffIcon,
  Clear as ClearIcon,
  Visibility as EyeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import Select from '../components/UIKit/Select';
import { 
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  MenuItem,
  Pagination,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ModulesList = () => {
  const { t } = useTranslation('modules');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalModules, setTotalModules] = useState(0);
  const [isDraft, setIsDraft] = useState(null);
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [authors, setAuthors] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showMyModules, setShowMyModules] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    loading: false,
    moduleId: null,
    moduleTitle: '',
    canDelete: false,
    courses: [],
    isForceDelete: false
  });

  const limit = 8;

  // Загрузка авторов
  const loadAuthors = async () => {
    try {
      const response = await usersAPI.getAll({ all: 'true', isCommon:1 });
      
      // Получаем всех пользователей без фильтрации по ролям
      const authorsList = response.data.data.users
        ?.map(user => ({
          id: user.id, 
          name: user.name
        })) || [];
      
      setAuthors(authorsList);
    } catch (error) {
      console.error('Ошибка при загрузке авторов:', error);
    }
  };

  // Загрузка модулей
  const loadModules = useCallback(async (page = 1, search = '', draftFilter = null, authorFilter = '') => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit,
        search: search || undefined,
        isDraft: draftFilter !== null ? draftFilter : undefined,
        // Если включен фильтр "My Modules", не используем фильтр по автору
        author: showMyModules ? undefined : (authorFilter || undefined)
      };

      // Добавляем параметр для отображения модулей пользователя
      if (showMyModules) {
        params.myModules = true;
      }

      const response = await modulesAPI.getAll(params);
      
      if (response.data) {
        setModules(response.data.modules || []);
        setTotalPages(response.data.pagination?.pages || 1);
        setTotalModules(response.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Ошибка при загрузке модулей:', err);
      setError(t('modulesPage.errors.loadError'));
      toast.error(t('modulesPage.errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [showMyModules, t]);

  // Загрузка при монтировании компонента
  useEffect(() => {
    loadModules(currentPage, searchTerm, isDraft, selectedAuthor);
  }, [loadModules, currentPage, isDraft, searchTerm, selectedAuthor, showMyModules]);

  // Загрузка авторов при монтировании
  useEffect(() => {
    loadAuthors();
  }, []);

  // Обработчики изменения фильтров
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // Обработка изменения фильтра черновиков
  const handleDraftFilterChange = (value) => {
    setIsDraft(value);
    setCurrentPage(1);
  };

  // Обработка изменения фильтра автора
  const handleAuthorFilterChange = (value) => {
    setSelectedAuthor(value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm !== '' ||
      selectedAuthor !== '' ||
      isDraft !== null ||
      showMyModules ||
      currentPage !== 1
    );
  }, [searchTerm, selectedAuthor, isDraft, showMyModules, currentPage]);

  // Обработчик переключения отображения расширенных фильтров
  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters(!showAdvancedFilters);
  };

  // Обработчик очистки всех фильтров
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedAuthor('');
    setIsDraft(null);
    setShowMyModules(false);
    setCurrentPage(1);
  };

  // Обработчик переключения отображения модулей пользователя
  const toggleMyModules = () => {
    setShowMyModules(!showMyModules);
    setCurrentPage(1);
    // Сбрасываем фильтр по автору при включении "My Modules"
    if (!showMyModules) {
      setSelectedAuthor('');
    }
  };

  // Открытие модального окна удаления
  const handleDeleteModule = async (moduleId, moduleTitle) => {
    try {
      // Проверяем возможность удаления
      const checkResponse = await modulesAPI.checkDelete(moduleId);
      const { canDelete, coursesUsingModule } = checkResponse.data;

      setDeleteModal({
        open: true,
        loading: false,
        moduleId,
        moduleTitle,
        canDelete,
        courses: coursesUsingModule,
        isForceDelete: false
      });
    } catch (err) {
      console.error('Ошибка при проверке возможности удаления:', err);
      toast.error('Ошибка при проверке возможности удаления');
    }
  };

  // Подтверждение удаления
  const handleConfirmDelete = async () => {
    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));

      if (!deleteModal.canDelete && !deleteModal.isForceDelete) {
        // Показываем второе предупреждение для принудительного удаления
        setDeleteModal(prev => ({ ...prev, isForceDelete: true, loading: false }));
        return;
      }

      // Выполняем удаление
      await modulesAPI.delete(deleteModal.moduleId, true, deleteModal.isForceDelete);
      
      toast.success(deleteModal.isForceDelete 
        ? t('deleteConfirmation.forceDeleteMessage') 
        : t('deleteConfirmation.message')
      );

      setDeleteModal({
        open: false,
        loading: false,
        moduleId: null,
        moduleTitle: '',
        canDelete: false,
        courses: [],
        isForceDelete: false
      });

      loadModules(currentPage, searchTerm, isDraft, selectedAuthor);
    } catch (err) {
      console.error('Ошибка при удалении модуля:', err);
      
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t('modulesPage.errors.loadError'));
      }
      
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Закрытие модального окна
  const handleCloseDeleteModal = () => {
    setDeleteModal({
      open: false,
      loading: false,
      moduleId: null,
      moduleTitle: '',
      canDelete: false,
      courses: [],
      isForceDelete: false
    });
  };

  // Проверка прав на редактирование
  const canEdit = (module) => {
    if (!user) return false;
    return user.userMeta?.roles?.includes('admin') ||
           user.userMeta?.roles?.includes('teacher') ||
           module.author?._id === user.userId;
  };



    return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography component="h1" variant="h3">
            {t('modulesPage.title')}
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Кнопка "Мои модули" для всех авторизованных пользователей */}
              <Button
                variant={showMyModules ? "contained" : "outlined"}
                color={showMyModules ? "secondary" : "primary"}
                onClick={toggleMyModules}
                data-testid="toggle-my-modules-button"
              >
                {showMyModules ? t('modulesPage.showAllModules') : t('modulesPage.myModules')}
              </Button>
              
              {/* Кнопка создания модуля для всех авторизованных пользователей */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                component={Link}
                to="/modules/create"
                data-testid="create-button"
              >
                {t('modulesPage.addModule')}
              </Button>
            </Box>
          )}
        </Box>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          {t('modulesPage.subtitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('modulesPage.stats.totalModules', { count: totalModules })}
        </Typography>

        {/* Фильтры и поиск */}
        <Box sx={{ mb: hasActiveFilters ? 0 : 4 }} className="filters">
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
            }}
          >
            <SearchInput
                  value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t('modulesPage.searchPlaceholder')}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={toggleAdvancedFilters}
              startIcon={
                showAdvancedFilters ? <FilterListOffIcon /> : <FilterIcon />
              }
              sx={{
                minWidth: { xs: '100%', sm: '180px' },
                height: { xs: '48px', sm: 'auto' },
                fontWeight: 500,
              }}
            >
              {showAdvancedFilters
                ? t('modulesPage.filters.hideFilters')
                : t('modulesPage.filters.showFilters')}
            </Button>

            {hasActiveFilters && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                sx={{
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  whiteSpace: 'nowrap',
                  minWidth: { xs: '100%', sm: 'auto' },
                  height: { xs: '48px', sm: 'auto' },
                }}
              >
                {t('modulesPage.filters.clearAll')}
              </Button>
            )}
          </Box>

          {/* Расширенные фильтры */}
          {showAdvancedFilters && (
            <Box sx={{ mb: 3 }}>
              {/* Фильтр по автору */}
              <Box sx={{ mb: 2, maxWidth: '300px' }}>
                <Select
                  label={t('modulesPage.filters.selectAuthor')}
                  value={selectedAuthor}
                  onChange={(e) => handleAuthorFilterChange(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: '0.875rem',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '30vh',
                        maxWidth: '250px',
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem',
                          minHeight: '32px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }
                      },
                      style: {
                        maxHeight: '30vh',
                        maxWidth: '250px',
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    disableScrollLock: true,
                    keepMounted: false,
                    MenuListProps: {
                      sx: {
                        maxHeight: '30vh',
                        overflowY: 'auto',
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('modulesPage.filters.allAuthors')}</em>
                  </MenuItem>
                  {authors.map((author) => (
                    <MenuItem key={author.id} value={author.id}>
                      {author.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

            {/* Фильтр по статусу */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
              <Button
                variant={isDraft === null ? "contained" : "outlined"}
                onClick={() => handleDraftFilterChange(null)}
                startIcon={<FilterIcon />}
                  sx={{ flex: 1 }}
              >
                  {t('modulesPage.filters.allStatuses')}
              </Button>
              <Button
                variant={isDraft === false ? "contained" : "outlined"}
                onClick={() => handleDraftFilterChange(false)}
                  sx={{ flex: 1 }}
              >
                  {t('modulesPage.filters.published')}
              </Button>
              <Button
                variant={isDraft === true ? "contained" : "outlined"}
                onClick={() => handleDraftFilterChange(true)}
                  sx={{ flex: 1 }}
              >
                  {t('modulesPage.filters.drafts')}
              </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Список модулей */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="h6" color="error">
              {t('modulesPage.errors.loadError')}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => loadModules(currentPage, searchTerm, isDraft, selectedAuthor)}
              sx={{ mt: 2 }}
            >
              {t('modulesPage.errors.tryAgain')}
            </Button>
          </Box>
        ) : modules.length === 0 ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="h6">
              {t('modulesPage.noModules.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('modulesPage.noModules.subtitle')}
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3} className="catalog">
            {modules.map((module) => (
                <Grid item xs={12} sm={6} md={4} xl={3} key={module._id}>
                  <ModuleDisplayCard
                    module={module}
                    variant="default"
                    showAuthor={true}
                    showStats={true}
                    showCourseInfo={true}
                    showDate={true}
                    canEdit={canEdit(module)}
                    customButtons={
                      <Box sx={{ position: 'relative', width: '100%', mt: 2 }}>
                        {/* Action buttons - BOTTOM RIGHT */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Tooltip title={t('modulesPage.actions.view')}>
                            <IconButton size="small" onClick={() => navigate(`/modules/${module.slug || module._id}`)}>
                              <EyeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {canEdit(module) && (
                            <Tooltip title={t('modulesPage.actions.edit')}>
                              <IconButton size="small" onClick={() => navigate(`/modules/${module.slug || module._id}/edit`)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                      
                      {canEdit(module) && (
                            <Tooltip title={t('modulesPage.actions.delete')}>
                              <IconButton size="small" onClick={() => handleDeleteModule(module._id, module.title)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    }
                    sx={{
                      opacity: module.isDraft ? 0.8 : 1,
                      "&:hover": {
                        transform: "none",
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>

        {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}

        {/* Модальное окно подтверждения удаления */}
        <DeleteConfirmationModal
          open={deleteModal.open}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title={`${t('modules:deleteConfirmation.title')} "${deleteModal.moduleTitle}"`}
          message={
            deleteModal.canDelete
              ? t('modules:deleteConfirmation.message')
              : deleteModal.isForceDelete
              ? t('modules:deleteConfirmation.forceDeleteMessage')
              : t('modules:deleteConfirmation.messageWithCourses')
          }
          courses={deleteModal.courses}
          isForceDelete={deleteModal.isForceDelete}
          loading={deleteModal.loading}
        />
      </Box>
    </Container>
  );
};

export default ModulesList;
